import { createContext, useContext, useState } from 'react';

import { Event, useEvent } from '@bizzabo/entities';

export enum View {
    Manage,
    ContactListPage,
    SchedulingWizard,
    MeetingInvitation,
    ZeroState,
}

export type SchedulingWizardArgs = {
    otherAttendeeId: number;
};

export type MeetingInvitationArgs = {
    meetingId: string;
};

export type MeetingsToolContextValues = {
    eventUniqueName: string;
    eventId: number;
    event: Event;
    view: View;
    setView: (view: View) => void;
    openWizard: (schedulingWizardArgs: SchedulingWizardArgs) => void;
    schedulingWizardArgs: SchedulingWizardArgs;
    openMeetingInvitation: (meetingInvitationArgs: MeetingInvitationArgs) => void;
    meetingInvitationArgs: MeetingInvitationArgs;
};

const MeetingsToolContext = createContext<MeetingsToolContextValues | null>(null);

export const MeetingsProvider: React.FC<{ eventUniqueName: string }> = ({
    eventUniqueName,
    children,
}) => {
    const { data: event } = useEvent(eventUniqueName);
    const [view, setView] = useState(View.Manage);
    const [schedulingWizardArgs, setSchedulingWizardArgs] = useState<SchedulingWizardArgs>({
        otherAttendeeId: null,
    });
    const [meetingInvitationArgs, setMeetingInvitationArgs] = useState<MeetingInvitationArgs>({
        meetingId: null,
    });

    const openWizard = (args: SchedulingWizardArgs) => {
        setSchedulingWizardArgs(args);
        setView(View.SchedulingWizard);
    };

    const openMeetingInvitation = (args: MeetingInvitationArgs) => {
        setMeetingInvitationArgs(args);
        setView(View.MeetingInvitation);
    };

    return (
        <MeetingsToolContext.Provider
            value={{
                eventUniqueName,
                eventId: event?.id,
                event: event,
                view,
                setView,
                openWizard,
                schedulingWizardArgs,
                openMeetingInvitation,
                meetingInvitationArgs,
            }}
        >
            {children}
        </MeetingsToolContext.Provider>
    );
};

export const useMeetings = () => {
    const context = useContext(MeetingsToolContext);
    if (!context) {
        throw new Error('useMeetings must be used within a MeetingsProvider');
    }
    return context;
};
